// Power BI — intégration et configuration
import { useState, useCallback } from "react";
import { BarChart3, ExternalLink, CheckCircle, AlertCircle, Save } from "lucide-react";
import { Button } from "@cieda/djayma-ui";
import AdminLayout from "@/components/admin/AdminLayout";

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function PowerBI() {
  const [workspaceUrl, setWorkspaceUrl] = useState(
    () => localStorage.getItem("powerbi_workspace_url") ?? "",
  );
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = useCallback(() => {
    if (!workspaceUrl.trim()) {
      setError("Veuillez saisir une URL de workspace Power BI.");
      setSaved(false);
      return;
    }
    if (
      !workspaceUrl.startsWith("https://app.powerbi.com") &&
      !workspaceUrl.startsWith("https://")
    ) {
      setError("L'URL doit commencer par https://");
      setSaved(false);
      return;
    }
    setError(null);
    localStorage.setItem("powerbi_workspace_url", workspaceUrl.trim());
    setSaved(true);
  }, [workspaceUrl]);

  const isConfigured = Boolean(workspaceUrl.trim() && saved);

  return (
    <AdminLayout>
      <div className="space-y-8">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
            <BarChart3 className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display text-xl font-semibold text-stone-900">
              Intégration Power BI
            </h1>
            <p className="text-sm text-stone-500">
              Connectez votre espace Power BI pour visualiser vos données en
              temps réel
            </p>
          </div>
        </div>

        {/* Intro card */}
        <div className="rounded-lg border border-blue-100 bg-blue-50 px-5 py-4">
          <h2 className="mb-2 text-sm font-semibold text-blue-900">
            À propos de l'intégration Power BI
          </h2>
          <ul className="space-y-1.5 text-sm text-blue-800">
            <li className="flex items-start gap-2">
              <span className="mt-0.5 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-500" />
              Connectez vos données Djayma à Microsoft Power BI pour des
              visualisations avancées.
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-0.5 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-500" />
              Tableaux de bord en temps réel avec actualisation automatique.
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-0.5 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-500" />
              Partagez vos rapports avec votre équipe et la direction.
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-0.5 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-500" />
              Analysez ventes, marges, dépenses et trésorerie en un seul
              tableau de bord.
            </li>
          </ul>
          <a
            href="https://powerbi.microsoft.com"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-blue-700 hover:underline"
          >
            Accéder à Power BI
            <ExternalLink className="h-3 w-3" />
          </a>
        </div>

        {/* Configuration */}
        <div className="rounded-lg border border-stone-200 bg-white p-5">
          <h2 className="mb-4 text-sm font-semibold text-stone-800">
            Configuration
          </h2>
          <div className="space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-stone-700">
                URL du workspace Power BI
              </label>
              <div className="flex gap-2">
                <input
                  type="url"
                  value={workspaceUrl}
                  onChange={(e) => {
                    setWorkspaceUrl(e.target.value);
                    setSaved(false);
                    setError(null);
                  }}
                  placeholder="https://app.powerbi.com/groups/…"
                  className="h-9 flex-1 rounded-md border border-stone-200 px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
                />
                <Button onClick={handleSave} className="gap-1.5">
                  <Save className="h-4 w-4" />
                  Enregistrer
                </Button>
              </div>
              {error && (
                <p className="mt-1.5 flex items-center gap-1.5 text-xs text-red-600">
                  <AlertCircle className="h-3.5 w-3.5" />
                  {error}
                </p>
              )}
              {saved && !error && (
                <p className="mt-1.5 flex items-center gap-1.5 text-xs text-emerald-600">
                  <CheckCircle className="h-3.5 w-3.5" />
                  URL enregistrée avec succès.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Preview / placeholder */}
        {isConfigured ? (
          <div className="space-y-3">
            <h2 className="text-sm font-semibold text-stone-700">
              Aperçu du workspace
            </h2>
            <div className="overflow-hidden rounded-lg border border-stone-200 bg-stone-50 p-4">
              <div className="mb-3 flex items-center justify-between">
                <p className="text-xs text-stone-500">
                  Workspace configuré :{" "}
                  <span className="font-medium text-stone-700">
                    {workspaceUrl}
                  </span>
                </p>
                <a
                  href={workspaceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 rounded px-3 py-1.5 text-xs font-medium text-amber-700 hover:bg-amber-50"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  Ouvrir dans Power BI
                </a>
              </div>
              <div className="flex h-64 items-center justify-center rounded-md border border-dashed border-stone-300 bg-white">
                <div className="text-center">
                  <BarChart3 className="mx-auto mb-2 h-8 w-8 text-stone-300" />
                  <p className="text-sm text-stone-400">
                    Intégration Power BI non disponible en iframe.
                  </p>
                  <p className="text-xs text-stone-400">
                    Ouvrez le workspace directement dans Power BI.
                  </p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-stone-300 bg-stone-50 py-16 text-center">
            <BarChart3 className="mb-3 h-10 w-10 text-stone-300" />
            <p className="text-sm font-medium text-stone-600">
              Aucun workspace configuré
            </p>
            <p className="mt-1 max-w-sm text-xs text-stone-400">
              Saisissez l'URL de votre workspace Power BI ci-dessus pour
              activer l'intégration et visualiser vos données Djayma.
            </p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
